// react
import { Suspense, useCallback } from 'react';
import { useSelector } from 'react-redux';

// tanstack
import {
  createFileRoute,
  Navigate,
  Outlet,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';

// api
import { useGetLinkedEnergyGroupsLinkedEnergyGroupsAccountIdGetQuery } from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysisMeters';

// shared components
import { ServiceGuard } from '@/components/access-control/ServiceGuard';

// feature-components
import EnergyAnalysisAppBar from '@/features/energyxplorer/components/EnergyAnalysisAppBar';

// components
import MeterDrawer from '@/features/energyxplorer/portfolio/components/meter-drawer/MeterDrawer';
import useMatrikelList from '@/features/energyxplorer/portfolio/hooks/useMatrikelList';
import type {
  AnalysisType,
  EnrichedMeter,
} from '@/features/energyxplorer/types';
import { getSelectedAccount } from '@/redux/selectedAccountSlice';

type LinkedEnergyGroupsData = {
  LinkedEnergyGroups?: Array<{
    MatrikelKey: string;
    LinkedEnergyGroups: Array<{ type?: string }>;
  }>;
};

function PortfolioMeterDrawerContainer() {
  const search = useSearch({ strict: false });
  const navigate = useNavigate();
  const account = useSelector(getSelectedAccount);

  const meterId = search.meterId as string | undefined;
  const meterSubId = search.meterSubId as string | undefined;
  const meterRowType = ((search.meterRowType as string) || 'matrikel') as
    'matrikel' | 'meter';
  const drawerAnalysisType = ((search.drawerAnalysisType as string) ||
    'power') as AnalysisType;

  const { matrikel_list } = useMatrikelList();
  const { data: linkedEnergyGroupsData } =
    useGetLinkedEnergyGroupsLinkedEnergyGroupsAccountIdGetQuery(
      { accountId: account?.AccountId },
      { skip: !account?.AccountId },
    );

  const meter = (() => {
    if (!meterId || !matrikel_list) return null;
    const matrikelItem = matrikel_list.find(
      (m) => m.metadata.MatrikelKey === meterId,
    );
    if (!matrikelItem) return null;

    const meta = matrikelItem.metadata as unknown as Record<string, unknown>;
    const linkedEnergyGroups = (
      linkedEnergyGroupsData as LinkedEnergyGroupsData | undefined
    )?.LinkedEnergyGroups?.find(
      (leg) => leg.MatrikelKey === meterId,
    )?.LinkedEnergyGroups?.filter(
      (group) => !group.type?.toLowerCase().includes('portfolio'),
    );

    if (meterRowType === 'meter' && meterSubId) {
      for (const energyType of ['power', 'water', 'heat'] as const) {
        const meterList = matrikelItem[energyType]?.meter_list ?? [];
        const entry = meterList.find(
          (m) =>
            String(m.metadata.UnitId) === meterSubId ||
            String(m.metadata.MeteringPointId) === meterSubId,
        );
        if (entry) {
          return {
            ...entry.analysis,
            ...entry.metadata,
            matrikel_key: meterId,
            main_address: meta.Address || meta.MainAddress,
            BFENummer: meta.BFENummer,
            type: energyType,
            linked_energy_groups: linkedEnergyGroups,
          };
        }
      }
    }

    return {
      matrikel_key: meterId,
      main_address: meta.Address || meta.MainAddress,
      BFENummer: meta.BFENummer,
      type: drawerAnalysisType,
      linked_energy_groups: linkedEnergyGroups,
    };
  })();

  const handleClose = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (navigate as any)({
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        meterId: undefined,
        meterSubId: undefined,
        meterRowType: undefined,
        drawerAnalysisType: undefined,
        editMode: undefined,
        editBuilding: undefined,
        selectedChart: undefined,
      }),
    });
  }, [navigate]);
  if (!meterId || !meter) return null;

  return (
    <MeterDrawer
      key={meterId}
      open
      onClose={handleClose}
      meter={meter as unknown as EnrichedMeter & Record<string, unknown>}
      analysisType={drawerAnalysisType}
    />
  );
}

export const Route = createFileRoute(
  '/$accountId/_user/energyxplorer/portfolio',
)({
  component: () => (
    <ServiceGuard
      serviceNames={['PortfolioAnalysis']}
      fallback={<Navigate to="/$accountId" />}
    >
      <Suspense>
        <EnergyAnalysisAppBar />
      </Suspense>
      <Suspense>
        <Outlet />
      </Suspense>
      <Suspense>
        <PortfolioMeterDrawerContainer />
      </Suspense>
    </ServiceGuard>
  ),
  staticData: { crumb: 'glossary:portfolio', hideEnergyAnalysisAppBar: true },
});
